import { reminderOccurrenceKey, type ReminderNotice, type WorkflowReminder } from "./models";

type ReminderTransport = {
    claim: (signal: AbortSignal) => Promise<WorkflowReminder[]>;
    dismiss: (reminder: WorkflowReminder, signal: AbortSignal) => Promise<void>;
};

const POLL_INTERVAL_MS = 15_000;
// The server grants a 60-second display lease. Hide before an unrenewed lease expires.
const DISPLAY_LEASE_MS = 55_000;

/** One authenticated page session; pausing relinquishes its local presentation. */
export class ReminderSession {
    private active = false;
    private disposed = false;
    private unauthorized = false;
    private notices: ReminderNotice[] = [];
    private readonly dismissed = new Set<string>();
    private readonly dismissRequests = new Map<string, AbortController>();
    private claimRequest: AbortController | null = null;
    private timer: ReturnType<typeof setTimeout> | undefined;
    private leaseTimer: ReturnType<typeof setTimeout> | undefined;
    private requestTimer: ReturnType<typeof setTimeout> | undefined;

    constructor(
        private readonly transport: ReminderTransport,
        private readonly onChange: (notices: ReminderNotice[]) => void
    ) {}

    resume(): void {
        if (this.disposed || this.active || this.unauthorized) return;
        this.active = true;
        void this.claim();
    }

    pause(): void {
        this.active = false;
        clearTimeout(this.timer);
        clearTimeout(this.leaseTimer);
        clearTimeout(this.requestTimer);
        this.claimRequest?.abort();
        this.claimRequest = null;
        for (const request of this.dismissRequests.values()) request.abort();
        this.dismissRequests.clear();
        this.publish([]);
    }

    dispose(): void {
        this.disposed = true;
        this.pause();
    }

    async dismiss(key: string): Promise<void> {
        if (!this.active || this.disposed || this.dismissRequests.has(key)) return;
        const notice = this.notices.find(item => reminderOccurrenceKey(item.reminder) === key);
        if (!notice) return;
        const request = new AbortController();
        this.dismissRequests.set(key, request);
        this.updateNotice(key, { dismissing: true, dismissFailed: false });
        const timeout = setTimeout(() => {
            if (!this.isCurrent(request)) return;
            request.abort();
            if (this.dismissRequests.get(key) === request) this.dismissRequests.delete(key);
            this.updateNotice(key, { dismissing: false, dismissFailed: true });
        }, POLL_INTERVAL_MS);
        try {
            await this.transport.dismiss(notice.reminder, request.signal);
            if (!this.isCurrent(request)) return;
            // A concurrent claim response may still contain the just-dismissed item.
            this.dismissed.add(key);
            this.publish(this.notices.filter(item => reminderOccurrenceKey(item.reminder) !== key));
        } catch {
            if (this.isCurrent(request)) {
                this.dismissRequests.delete(key);
                this.updateNotice(key, { dismissing: false, dismissFailed: true });
            }
        } finally {
            clearTimeout(timeout);
            if (this.dismissRequests.get(key) === request) this.dismissRequests.delete(key);
        }
    }

    private async claim(): Promise<void> {
        if (!this.active || this.disposed || this.claimRequest) return;
        const request = new AbortController();
        const startedAt = Date.now();
        this.claimRequest = request;
        this.requestTimer = setTimeout(() => {
            if (this.claimRequest !== request) return;
            request.abort();
            this.claimRequest = null;
            if (this.active && !this.disposed) this.timer = setTimeout(() => void this.claim(), POLL_INTERVAL_MS);
        }, POLL_INTERVAL_MS);
        try {
            const reminders = await this.transport.claim(request.signal);
            if (!this.isCurrent(request)) return;
            clearTimeout(this.leaseTimer);
            this.leaseTimer = setTimeout(() => this.publish([]), Math.max(0, DISPLAY_LEASE_MS - (Date.now() - startedAt)));
            const previous = new Map(this.notices.map(notice => [reminderOccurrenceKey(notice.reminder), notice]));
            this.publish(
                reminders
                    .filter(reminder => !this.dismissed.has(reminderOccurrenceKey(reminder)))
                    .map(reminder => ({
                        reminder,
                        dismissing: this.dismissRequests.has(reminderOccurrenceKey(reminder)),
                        dismissFailed: previous.get(reminderOccurrenceKey(reminder))?.dismissFailed ?? false
                    }))
            );
        } catch (error: unknown) {
            if (this.isCurrent(request) && isAuthorizationError(error)) {
                this.unauthorized = true;
                this.pause();
            }
        } finally {
            if (this.claimRequest === request) {
                clearTimeout(this.requestTimer);
                this.claimRequest = null;
                if (this.active && !this.disposed) this.timer = setTimeout(() => void this.claim(), POLL_INTERVAL_MS);
            }
        }
    }

    private isCurrent(request: AbortController): boolean {
        return this.active && !this.disposed && !request.signal.aborted;
    }

    private updateNotice(key: string, update: Pick<ReminderNotice, "dismissing" | "dismissFailed">): void {
        this.publish(this.notices.map(notice => (reminderOccurrenceKey(notice.reminder) === key ? { ...notice, ...update } : notice)));
    }

    private publish(notices: ReminderNotice[]): void {
        this.notices = notices;
        // Hide immediately while saving. Keep the latest claimed notice for a failed-request retry.
        if (!this.disposed) this.onChange(notices.filter(notice => !this.dismissRequests.has(reminderOccurrenceKey(notice.reminder))));
    }
}

function isAuthorizationError(error: unknown): boolean {
    return error instanceof Error && "status" in error && (error.status === 401 || error.status === 403);
}
